import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import { campaigns, Campaign, CampaignTask, TaskStatus } from "../../mocks/campaigns";
import { AnnouncementModal, PracticeFormModal } from "../../components/feature/TaskFormModals";
import { logActivity } from "../../lib/activityLog";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";

type DetailTab = "brief" | "tasks" | "timeline" | "team";

const statusColor: Record<string, string> = {
  "기획중": "text-amber-400/80 bg-amber-400/10 border-amber-400/20",
  "진행중": "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20",
  "후반작업": "text-violet-400/80 bg-violet-400/10 border-violet-400/20",
  "완료": "text-[#444444] border-white/[0.05]",
};

const taskStatusMeta: Record<TaskStatus, { label: string; color: string; icon: string }> = {
  todo: { label: "대기", color: "text-[#555555] border-white/[0.08]", icon: "ri-checkbox-blank-circle-line" },
  inprogress: { label: "진행중", color: "text-amber-400/80 border-amber-400/20 bg-amber-400/5", icon: "ri-loader-4-line" },
  done: { label: "완료", color: "text-emerald-400/70 border-emerald-400/20 bg-emerald-400/5", icon: "ri-checkbox-circle-line" },
  overdue: { label: "기한초과", color: "text-red-400/70 border-red-400/20 bg-red-400/5", icon: "ri-error-warning-line" },
};

function DDayBadge({ dDay, status }: { dDay: number; status: TaskStatus }) {
  if (status === "done") return <span className="text-[10px] text-emerald-400/60">완료</span>;
  if (dDay < 0) return <span className="text-[10px] text-red-400/70 font-medium">D+{Math.abs(dDay)}</span>;
  if (dDay === 0) return <span className="text-[10px] text-amber-400 font-bold">D-Day</span>;
  if (dDay <= 3) return <span className="text-[10px] text-amber-400/80 font-medium">D-{dDay}</span>;
  return <span className="text-[10px] text-[#444444]">D-{dDay}</span>;
}

function TaskCard({
  task,
  onToggle,
  onShowAnnouncement,
  onShowPractice,
}: {
  task: CampaignTask;
  onToggle: (id: string) => void;
  onShowAnnouncement: (task: CampaignTask) => void;
  onShowPractice: (task: CampaignTask) => void;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const meta = taskStatusMeta[task.status];
  const isDone = task.status === "done";
  return (
    <div className={`bg-[#0C0C0E] border rounded-xl transition-all ${isDone ? "border-white/[0.04] opacity-60" : task.dDay <= 3 ? "border-amber-400/15" : "border-white/[0.07]"}`}>
      {/* 기본 정보 행 (클릭하면 확장) */}
      <div
        className="p-4 cursor-pointer"
        onClick={() => !isDone && setExpanded(e => !e)}
      >
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onToggle(task.id); }}
            className={`mt-0.5 w-5 h-5 flex items-center justify-center rounded-full border shrink-0 cursor-pointer transition-all ${meta.color}`}
          >
            <i className={`${meta.icon} text-xs`}></i>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-sm font-medium ${isDone ? "text-[#444444] line-through" : "text-[#E0E0E0]"}`}>{task.title}</span>
              <DDayBadge dDay={task.dDay} status={task.status} />
            </div>
            <p className="text-[#555555] text-xs leading-relaxed mb-2">{task.detail}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[#555555] text-[11px]"><i className="ri-calendar-line mr-1"></i>{task.dueDate}</span>
              <span className="text-[#555555] text-[11px]"><i className="ri-user-line mr-1"></i>{task.assignee}</span>
            </div>
          </div>
          {!isDone && task.steps && task.steps.length > 0 && (
            <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-[#555555] text-base shrink-0 mt-0.5`}></i>
          )}
        </div>
      </div>

      {/* 확장 패널: 업무 가이드 */}
      {expanded && task.steps && (
        <div className="border-t border-white/[0.05] px-4 pb-4 pt-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 flex items-center justify-center rounded bg-[#4B6CF7]/15">
              <i className="ri-route-line text-[#7A9BFF] text-[10px]"></i>
            </div>
            <span className="text-[#555555] text-[10px] font-semibold uppercase tracking-widest">업무 진행 가이드</span>
          </div>
          <div className="space-y-0">
            {task.steps.map(step => (
              <div key={step.num} className="flex gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-6 h-6 rounded-full bg-[#4B6CF7]/10 border border-[#4B6CF7]/20 flex items-center justify-center shrink-0">
                    <span className="text-[#7A9BFF]/80 text-[10px] font-bold">{step.num}</span>
                  </div>
                  <div className="w-px flex-1 bg-white/[0.04] mt-1 mb-1 min-h-[8px]"></div>
                </div>
                <div className="flex-1 pb-3">
                  <div className="text-[#BBBBBB] text-xs font-medium mb-1">{step.title}</div>
                  <p className="text-[#555555] text-xs leading-relaxed">{step.detail}</p>
                  {step.tip && (
                    <div className="mt-2 flex gap-1.5 bg-[#4B6CF7]/5 border border-[#4B6CF7]/10 rounded-lg px-2.5 py-2">
                      <i className="ri-lightbulb-flash-line text-[#7A9BFF]/60 text-[10px] mt-0.5 shrink-0"></i>
                      <p className="text-[#7A9BFF]/60 text-[10px] leading-relaxed">{step.tip}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {task.caution && (
            <div className="flex gap-2 bg-amber-400/[0.04] border border-amber-400/15 rounded-lg px-3 py-2 mb-3">
              <i className="ri-error-warning-line text-amber-400/60 text-xs mt-0.5 shrink-0"></i>
              <p className="text-[#888888] text-[11px] leading-relaxed">{task.caution}</p>
            </div>
          )}
          {/* 산출물 + 결재 버튼 */}
          {(task.deliverable || task.approvalTemplateId || task.announcement || task.practiceForm) && (
            <div className="flex flex-col gap-2 pt-2 border-t border-white/[0.04]">
              {/* 공고문 / 실습 양식 버튼 */}
              {(task.announcement || task.practiceForm) && (
                <div className="flex gap-2 flex-wrap">
                  {task.announcement && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onShowAnnouncement(task); }}
                      className="flex items-center gap-1.5 text-[11px] text-[#CCCCCC] bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-list-3-line text-xs text-[#7A9BFF]/70"></i>브리프 분석
                    </button>
                  )}
                  {task.practiceForm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onShowPractice(task); }}
                      className="flex items-center gap-1.5 text-[11px] text-[#1A1A1A] bg-white hover:bg-white/90 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold"
                    >
                      <i className="ri-edit-box-line text-xs"></i>직접 작성해보기
                    </button>
                  )}
                </div>
              )}
              {/* 산출물 + 결재 */}
              <div className="flex items-center justify-between gap-3">
                {task.deliverable && (
                  <span className="text-[11px] text-[#4A5568] flex items-center gap-1">
                    <i className="ri-file-text-line text-[#4B6CF7]/40"></i>{task.deliverable}
                  </span>
                )}
                {task.approvalTemplateId && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigate("/approval", { state: { templateId: task.approvalTemplateId } }); }}
                    className="shrink-0 flex items-center gap-1.5 text-[11px] text-white bg-[#4B6CF7] hover:bg-[#5B7CF7] px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line text-xs"></i>결재 제출
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CampaignHub() {
  const { user } = useAuth();
  const { workspaceId } = useCompany();
  const [selected, setSelected] = useState<Campaign>(campaigns[0]);
  const [detailTab, setDetailTab] = useState<DetailTab>("tasks");
  const [taskStates, setTaskStates] = useState<Record<string, TaskStatus>>({});
  const [overrides, setOverrides] = useState<Record<string, Partial<CampaignTask>>>({});
  const [announcementTask, setAnnouncementTask] = useState<CampaignTask | null>(null);
  const [practiceTask, setPracticeTask] = useState<CampaignTask | null>(null);

  // Fetch guide overrides
  useEffect(() => {
    if (!workspaceId) return;
    const allIds = campaigns.flatMap(c => c.tasks.map(t => t.id));
    supabase
      .from("hub_guide_overrides")
      .select("task_id, title, detail, caution, steps")
      .eq("workspace_id", workspaceId)
      .eq("hub_type", "campaign")
      .in("task_id", allIds)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, Partial<CampaignTask>> = {};
        data.forEach(row => {
          map[row.task_id] = {
            ...(row.title ? { title: row.title } : {}),
            ...(row.detail ? { detail: row.detail } : {}),
            ...(row.caution ? { caution: row.caution } : {}),
            ...(Array.isArray(row.steps) && row.steps.length > 0 ? { steps: row.steps } : {}),
          };
        });
        setOverrides(map);
      });
  }, [workspaceId]);

  // Fetch completions
  const fetchCompletions = useCallback(async () => {
    if (!workspaceId || !user?.id) return;
    const allIds = campaigns.flatMap(c => c.tasks.map(t => t.id));
    const { data } = await supabase
      .from("task_completions")
      .select("task_id")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id)
      .eq("hub", "campaign")
      .in("task_id", allIds);
    if (data) {
      const map: Record<string, TaskStatus> = {};
      data.forEach(row => { map[row.task_id] = "done"; });
      setTaskStates(map);
    }
  }, [workspaceId, user?.id]);

  useEffect(() => { fetchCompletions(); }, [fetchCompletions]);

  const getTaskStatus = (task: CampaignTask): TaskStatus => taskStates[task.id] ?? task.status;

  const toggleTask = async (id: string) => {
    const cur = taskStates[id] ?? campaigns.flatMap(c => c.tasks).find(t => t.id === id)?.status ?? "todo";
    const next: TaskStatus = cur === "done" ? "todo" : "done";
    setTaskStates(prev => ({ ...prev, [id]: next }));

    if (next === "done") {
      const taskTitle = campaigns.flatMap(c => c.tasks).find(t => t.id === id)?.title ?? "태스크";
      logActivity(`캠페인 허브 태스크 완료: ${taskTitle}`, "task_complete");
    }

    if (workspaceId && user?.id) {
      if (next === "done") {
        await supabase.from("task_completions").upsert({
          workspace_id: workspaceId,
          user_id: user.id,
          task_id: id,
          hub: "campaign",
          completed_at: new Date().toISOString(),
        }, { onConflict: "workspace_id,user_id,task_id" });
      } else {
        await supabase.from("task_completions").delete()
          .eq("workspace_id", workspaceId)
          .eq("user_id", user.id)
          .eq("task_id", id);
      }
    }
  };

  const mergeTask = (task: CampaignTask): CampaignTask => {
    const ov = overrides[task.id];
    return ov ? { ...task, ...ov } : task;
  };

  const tasks = selected.tasks.map(t => ({ ...mergeTask(t), status: getTaskStatus(t) }));
  const doneCount = tasks.filter(t => t.status === "done").length;
  const progress = tasks.length > 0 ? Math.round((doneCount / tasks.length) * 100) : 0;

  const detailTabs: { id: DetailTab; label: string; icon: string }[] = [
    { id: "tasks", label: "내 할 일", icon: "ri-checkbox-multiple-line" },
    { id: "brief", label: "캠페인 브리프", icon: "ri-file-info-line" },
    { id: "timeline", label: "마일스톤", icon: "ri-map-pin-time-line" },
    { id: "team", label: "팀 & 예산", icon: "ri-team-line" },
  ];

  const totalBudgetSpent = selected.budgetBreakdown.reduce((s, b) => s + b.amount, 0);

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 flex overflow-hidden" style={{ height: "calc(100vh - 48px)" }}>

          {/* ── Left: Campaign List ─────────────────────── */}
          <div className="w-72 shrink-0 border-r border-white/[0.05] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-white/[0.05]">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20">
                  <i className="ri-advertisement-line text-[#7A9BFF] text-sm"></i>
                </div>
                <span className="text-[#F2F2F2] text-sm font-semibold">캠페인 허브</span>
              </div>
              <div className="flex gap-3 text-[11px]">
                <span className="text-[#555555]">진행 중 <span className="text-[#888888] font-medium">{campaigns.filter(c => c.status !== "완료").length}건</span></span>
                <span className="text-[#555555]">총 수주 <span className="text-[#888888] font-medium">{campaigns.reduce((s, c) => s + c.budget, 0).toLocaleString()}만</span></span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-2 space-y-0.5 px-2">
              {campaigns.map((c) => {
                const isActive = selected.id === c.id;
                const cTasks = c.tasks.map(t => ({ ...t, status: getTaskStatus(t) }));
                const pendingTasks = cTasks.filter(t => t.status !== "done").length;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setSelected(c); setDetailTab("tasks"); }}
                    className={`w-full text-left px-3 py-3 rounded-xl transition-all cursor-pointer ${
                      isActive ? "bg-[#4B6CF7]/10 border border-[#4B6CF7]/20" : "hover:bg-white/[0.03] border border-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className={`text-xs font-medium leading-snug flex-1 ${isActive ? "text-[#C8D8FF]" : "text-[#CCCCCC]"}`}>{c.name}</span>
                      {pendingTasks > 0 && c.status !== "완료" && (
                        <span className="shrink-0 text-[9px] font-bold bg-amber-400/15 text-amber-400/80 border border-amber-400/20 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          할 일 {pendingTasks}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#555555] text-[10px]">{c.client}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border ${statusColor[c.status]}`}>{c.status}</span>
                    </div>
                    <div className="text-[#555555] text-[10px] mt-1">마감 {c.deadline}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right: Campaign Detail ─────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Campaign Header */}
            <div className="px-6 py-4 border-b border-white/[0.05] bg-[#111112] shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h1 className="text-[#F2F2F2] font-semibold text-base">{selected.name}</h1>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${statusColor[selected.status]}`}>{selected.status}</span>
                    <span className="text-[#555555] text-[10px] border border-white/[0.06] px-1.5 py-0.5 rounded">{selected.type}</span>
                  </div>
                  <p className="text-[#555555] text-xs">{selected.client} · PM {selected.pm} · 마감 {selected.deadline}</p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[#555555] text-[10px]">수주 금액</div>
                  <div className="text-[#888888] font-semibold text-sm tabular-nums">{selected.budget.toLocaleString()}만원</div>
                </div>
              </div>
              {/* Progress */}
              {tasks.length > 0 && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 bg-white/[0.04] rounded-full h-1">
                    <div className="h-1 rounded-full bg-[#4B6CF7]/60 transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="text-[11px] text-[#444444] shrink-0">{doneCount}/{tasks.length} 완료</span>
                </div>
              )}
            </div>

            {/* Detail Tabs */}
            <div className="px-6 pt-3 pb-0 border-b border-white/[0.05] bg-[#111112] shrink-0">
              <div className="flex gap-0.5">
                {detailTabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setDetailTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-all cursor-pointer whitespace-nowrap ${
                      detailTab === tab.id
                        ? "text-[#7A9BFF] border-b-2 border-[#4B6CF7]"
                        : "text-[#444444] hover:text-[#888888]"
                    }`}
                  >
                    <i className={`${tab.icon} text-sm`}></i>{tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Detail Content */}
            <div className="flex-1 overflow-y-auto p-6">

              {/* 내 할 일 */}
              {detailTab === "tasks" && (
                <div className="space-y-3 max-w-2xl">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-[#888888] text-xs font-medium uppercase tracking-widest">할 일 목록</h2>
                    <span className="text-[#555555] text-[11px]">완료 항목 클릭으로 토글</span>
                  </div>
                  {tasks.length === 0 ? (
                    <div className="text-[#555555] text-sm text-center py-12">등록된 할 일이 없습니다</div>
                  ) : (
                    tasks.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onToggle={toggleTask}
                        onShowAnnouncement={setAnnouncementTask}
                        onShowPractice={setPracticeTask}
                      />
                    ))
                  )}

                  {/* Objective reminder */}
                  <div className="mt-6 bg-[#4B6CF7]/5 border border-[#4B6CF7]/15 rounded-xl p-4">
                    <div className="text-[#4B6CF7]/60 text-[10px] uppercase tracking-widest mb-2">캠페인 목표</div>
                    <p className="text-[#9AABCC] text-sm leading-relaxed">{selected.objective}</p>
                  </div>
                </div>
              )}

              {/* 캠페인 브리프 */}
              {detailTab === "brief" && (
                <div className="max-w-2xl space-y-5">
                  <div className="bg-[#111112] border border-white/[0.06] rounded-xl p-5 space-y-4">
                    <div>
                      <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-1.5">브리프 개요</div>
                      <p className="text-[#AAAAAA] text-sm leading-relaxed">{selected.brief}</p>
                    </div>
                    <div className="border-t border-white/[0.05] pt-4">
                      <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">캠페인 목표</div>
                      <p className="text-[#888888] text-sm">{selected.objective}</p>
                    </div>
                    <div className="border-t border-white/[0.05] pt-4">
                      <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">핵심 메시지</div>
                      <p className="text-[#C8D8FF] text-sm font-medium italic">"{selected.keyMessage}"</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#111112] border border-white/[0.06] rounded-xl p-4">
                      <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-3">집행 채널</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selected.channels.map(ch => (
                          <span key={ch} className="text-[11px] text-[#7A9BFF]/70 bg-[#4B6CF7]/10 border border-[#4B6CF7]/15 px-2 py-0.5 rounded-full">{ch}</span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-[#111112] border border-white/[0.06] rounded-xl p-4">
                      <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-3">타겟 오디언스</div>
                      <p className="text-[#888888] text-xs leading-relaxed">{selected.targetAudience}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 마일스톤 */}
              {detailTab === "timeline" && (
                <div className="max-w-lg">
                  <h2 className="text-[#888888] text-xs font-medium uppercase tracking-widest mb-5">주요 마일스톤</h2>
                  <div className="relative">
                    <div className="absolute left-[11px] top-0 bottom-0 w-px bg-white/[0.06]"></div>
                    <div className="space-y-4">
                      {selected.milestones.map((m, i) => (
                        <div key={i} className="flex items-start gap-4 pl-1">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 relative z-10 ${
                            m.done ? "bg-emerald-400/20 border-emerald-400/50" : "bg-[#0C0C0E] border-white/[0.12]"
                          }`}>
                            {m.done ? (
                              <i className="ri-check-line text-emerald-400/70 text-[10px]"></i>
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-white/20"></div>
                            )}
                          </div>
                          <div className={`flex-1 py-1 ${m.done ? "opacity-50" : ""}`}>
                            <div className="flex items-center gap-3">
                              <span className="text-[#444444] text-[11px] tabular-nums font-medium">{m.date}</span>
                              <span className={`text-sm ${m.done ? "text-[#555555] line-through" : "text-[#CCCCCC]"}`}>{m.label}</span>
                              {m.done && <span className="text-[10px] text-emerald-400/60">✓ 완료</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 팀 & 예산 */}
              {detailTab === "team" && (
                <div className="grid grid-cols-2 gap-5 max-w-3xl">
                  {/* Team */}
                  <div className="bg-[#111112] border border-white/[0.06] rounded-xl p-5">
                    <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-4">팀 구성</div>
                    <div className="space-y-3">
                      {selected.team.map((m, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.06] shrink-0">
                            <i className={`${m.icon} text-sm text-[#555555]`}></i>
                          </div>
                          <div>
                            <div className="text-[#CCCCCC] text-xs font-medium">{m.name}</div>
                            <div className="text-[#444444] text-[10px]">{m.role}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="bg-[#111112] border border-white/[0.06] rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[#555555] text-[10px] uppercase tracking-widest">예산 배분</div>
                      <div className="text-[#888888] text-xs font-semibold">{totalBudgetSpent.toLocaleString()}만원</div>
                    </div>
                    <div className="space-y-3">
                      {selected.budgetBreakdown.map((b, i) => {
                        const pct = Math.round((b.amount / totalBudgetSpent) * 100);
                        return (
                          <div key={i}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[#888888] text-xs">{b.category}</span>
                              <span className="text-[#CCCCCC] text-xs tabular-nums">{b.amount.toLocaleString()}만</span>
                            </div>
                            <div className="w-full bg-white/[0.04] rounded-full h-1 mb-0.5">
                              <div className="h-1 rounded-full bg-[#4B6CF7]/40" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-[#555555] text-[10px]">{b.note}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </main>
    </div>

    {/* 모달 */}
    {announcementTask?.announcement && (
      <AnnouncementModal doc={announcementTask.announcement} onClose={() => setAnnouncementTask(null)} />
    )}
    {practiceTask?.practiceForm && (
      <PracticeFormModal form={practiceTask.practiceForm} onClose={() => setPracticeTask(null)} />
    )}
  </div>
  );
}
